/* ==========================================================================
   The fleet-wide command ledger.

   `/api/commands/pending` answers "what is in flight" and nothing else — it
   filters to pending+sent by construction, so a queue built on it alone can
   never show a failure, which is the one thing an operator opens a C2 board
   to find.

   `/api/audit/commands` is the other half: every lifecycle event the platform
   recorded, each carrying its command id, robot, operator, resulting status
   and result payload, and — on the `issued` event — the verb and the params.
   Replaying that stream in timestamp order reconstitutes the full command
   row, history included. That is not a derivation the console invented; it is
   what an append-only audit log is for.

   The two are then merged with `pending` authoritative for anything it lists,
   because that query is the one the MQTT stream patches in place (see
   hooks/useCommandStream) and is therefore always the fresher of the two.
   ========================================================================== */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRead, type Read as StateRead } from '../../components/states/index.ts';
import { apiFetch } from '../../auth/apiFetch.ts';
import { usePendingCommands, type Command, type CommandStatus } from '../../hooks/useCommands.ts';

/* --------------------------------------------------------------------------
   Audit events
   -------------------------------------------------------------------------- */

export interface CommandEvent {
  id: number;
  command_id: number;
  robot_id: string;
  ts: string;
  /** `issued`, or the status the command moved into. */
  event: string;
  operator: string;
  status: CommandStatus | null;
  result: Record<string, unknown> | null;
  fields: Record<string, unknown>;
}

/**
 * How far back the board reads. The endpoint caps at 1000; 600 covers the
 * whole fixture and, on a real tenant, roughly the last shift. The number is
 * printed on the surface rather than hidden here — a window the operator
 * cannot see the edge of is a window that lies.
 */
export const AUDIT_WINDOW = 600;

async function fetchAudit(limit: number): Promise<CommandEvent[]> {
  const r = await apiFetch(`/api/audit/commands?limit=${limit}`);
  if (!r.ok) throw new Error(`status ${r.status}`);
  const body = (await r.json()) as { events?: CommandEvent[] };
  return body.events ?? [];
}

export function useCommandAudit(limit: number = AUDIT_WINDOW) {
  return useQuery({
    queryKey: ['command-audit', limit] as const,
    queryFn: () => fetchAudit(limit),
    /* Same cadence as the pending queue, so the board and the log below it
       can never disagree about what happened by more than one beat — and
       deliberately NOT `refetchIntervalInBackground`, so the shell's `L`
       hold (TopBar's focusManager gate) stops this poll with all the rest. */
    refetchInterval: 3_000,
  });
}

/* --------------------------------------------------------------------------
   The reconstructed row
   -------------------------------------------------------------------------- */

export interface LedgerCommand {
  id: number;
  robot_id: string;
  command: string;
  params: Record<string, unknown>;
  status: CommandStatus;
  issued_by: string;
  issued_at: string;
  updated_at: string;
  result: Record<string, unknown> | null;
  /** Every transition the audit log holds for this command, oldest first. */
  events: readonly CommandEvent[];
  /** True where the row came from `/api/commands/pending`, not the log. */
  live: boolean;
}

const RANK: Record<CommandStatus, number> = {
  pending: 0, sent: 1, acked: 2, completed: 3, failed: 3, cancelled: 3,
};

function isStatus(v: unknown): v is CommandStatus {
  return typeof v === 'string' && v in RANK;
}

function fold(events: readonly CommandEvent[], pending: readonly Command[]): LedgerCommand[] {
  const byId = new Map<number, LedgerCommand>();

  const ordered = [...events].sort(
    (a, b) => Date.parse(a.ts) - Date.parse(b.ts) || a.id - b.id,
  );

  for (const e of ordered) {
    let row = byId.get(e.command_id);
    if (row === undefined) {
      row = {
        id: e.command_id,
        robot_id: e.robot_id,
        command: '',
        params: {},
        status: 'pending',
        issued_by: e.operator,
        issued_at: e.ts,
        updated_at: e.ts,
        result: null,
        events: [],
        live: false,
      };
      byId.set(e.command_id, row);
    }
    (row.events as CommandEvent[]).push(e);

    if (e.event === 'issued') {
      row.issued_at = e.ts;
      row.issued_by = e.operator;
      const verb = e.fields['command'];
      if (typeof verb === 'string') row.command = verb;
      const p = e.fields['params'];
      if (typeof p === 'object' && p !== null) row.params = p as Record<string, unknown>;
    } else if (isStatus(e.status)) {
      /* Forward-only, exactly as the realtime cache patch is: a log that
         arrives out of order must not walk a completed command backwards. */
      if (RANK[e.status] >= RANK[row.status]) {
        row.status = e.status;
        row.updated_at = e.ts;
      }
      if (e.result !== null) row.result = e.result;
    }
  }

  /* The in-flight set wins wherever it overlaps: it is the query the MQTT
     stream patches, so it is never behind the log, and it can carry a command
     whose `issued` event has already fallen out of the audit window. */
  for (const c of pending) {
    const row = byId.get(c.id);
    if (row === undefined) {
      byId.set(c.id, {
        id: c.id,
        robot_id: c.robot_id,
        command: c.command,
        params: c.params,
        status: c.status,
        issued_by: c.issued_by,
        issued_at: c.issued_at,
        updated_at: c.updated_at,
        result: c.result,
        events: [],
        live: true,
      });
      continue;
    }
    row.live = true;
    row.command = row.command === '' ? c.command : row.command;
    if (RANK[c.status] >= RANK[row.status]) {
      row.status = c.status;
      row.updated_at = c.updated_at;
    }
    if (c.result !== null) row.result = c.result;
  }

  return [...byId.values()].sort((a, b) => b.id - a.id);
}

export interface Ledger {
  commands: readonly LedgerCommand[];
  events: readonly CommandEvent[];
  /**
   * The read, as `components/states` words it. The board used to print
   * `Reading the queue…` for as long as a dead API kept refusing — a spinner's
   * sentence over a read that had stopped — and the panels below it printed
   * empty-state lines for the same event. One read, one word, and the panels
   * ask this.
   */
  read: StateRead;
  /**
   * The IN-FLIGHT QUEUE's own read — `/api/commands/pending`.
   *
   * Separate from `read` because the board is folded from BOTH endpoints and
   * every figure on it is therefore counted across both: `Queue (0)` and
   * `0 OPEN` are measurements only if AT LEAST ONE of them answered, and
   * blanking them on the audit read alone would throw away a real queue
   * whenever the history endpoint went. `unread([read, pending])` is the test
   * the board's figures use; the Activity panel, which reads only the audit,
   * uses `read` on its own.
   */
  pending: StateRead;
  /** The audit read is still in flight and we have nothing yet. */
  loading: boolean;
  /** Either read failed — the message, verbatim. */
  error: string | null;
  /** Number of audit events the window actually returned. */
  eventCount: number;
  /** True where the window came back full, i.e. history is being clipped. */
  clipped: boolean;
}

export function useCommandLedger(): Ledger {
  const audit = useCommandAudit();
  const pending = usePendingCommands();

  const events = useMemo(() => audit.data ?? [], [audit.data]);
  const inFlight = useMemo(() => pending.data ?? [], [pending.data]);

  const commands = useMemo(() => fold(events, inFlight), [events, inFlight]);

  const read = useRead(audit, {
    path: '/api/audit/commands',
    everyMs: 3_000,
    subject: 'the command ledger',
    empty: commands.length === 0,
  });
  const pendingRead = useRead(pending, {
    path: '/api/commands/pending',
    everyMs: 3_000,
    subject: 'the in-flight queue',
    empty: inFlight.length === 0,
  });

  const err = audit.error ?? pending.error;
  return {
    commands,
    events,
    read,
    pending: pendingRead,
    loading: (audit.isLoading || pending.isLoading) && commands.length === 0,
    error: err === null || err === undefined ? null : String(err),
    eventCount: events.length,
    clipped: events.length >= AUDIT_WINDOW,
  };
}

/* --------------------------------------------------------------------------
   Cancel — the queue's one write beyond dispatch.

   `PATCH /api/commands/{id}` moves a command to a terminal state. The console
   only ever uses it for `cancelled`, and only on a command that is still in
   flight: withdrawing something already completed is not a thing an operator
   can want, and offering it would suggest the platform can undo a command a
   robot has already run.
   -------------------------------------------------------------------------- */

export function useCancelCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: number; robotId: string }): Promise<Command> => {
      const r = await apiFetch(`/api/commands/${id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled' }),
      });
      if (!r.ok) {
        const text = await r.text().catch(() => '');
        throw new Error(text || `status ${r.status}`);
      }
      return (await r.json()) as Command;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['commands', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['commands', vars.robotId] });
      void qc.invalidateQueries({ queryKey: ['command-audit'] });
    },
  });
}

/** Refresh every C2 query after a write this module did not own. */
export function useRefreshCommands(): (robotId?: string) => void {
  const qc = useQueryClient();
  return useMemo(
    () => (robotId?: string) => {
      void qc.invalidateQueries({ queryKey: ['commands', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['command-audit'] });
      if (robotId !== undefined) void qc.invalidateQueries({ queryKey: ['commands', robotId] });
    },
    [qc],
  );
}
